import { Frequency } from '@generated/graphql';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { US_DATE_FORMAT } from '@ovComponents/4-module/workflowCompletion/constants/dateFormats';
import { DEPOSIT_TRANSFER_TYPE } from '@ovComponents/4-module/workflowCompletion/constants/deposits';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import {
  addBusinessDays,
  isBusinessDayAndAllowWeekendSync,
  isBusinessDaySync,
} from '@utils/businessDays';
import dayjs, { type ConfigType } from 'dayjs';
import utc from 'dayjs/plugin/utc';

export type DepositScheduleOptions = {
  allowOneTimeTransfersNow?: boolean;
  allowOneTimeTransfersFutureDate?: boolean;
  transferDelay?: { enabled?: boolean; number?: number } | null;
  editDefaultScheduleFrequency?: boolean;
  defaultScheduleOption?: Frequency | null;
  scheduledDate?: { required?: FieldRequiredType | string | null } | null;
};

export type TransferDelayCheck = {
  dateIsAfter: boolean;
  minTransferDate: string;
};

export type ScheduledDateErrorMessage = {
  key:
    | 'bankAccountTransferCreation.transferDelayError'
    | 'bankAccountTransferCreation.enterAValidDate';
  params?: { date: string };
};

// `@utils/businessDays` also registers utc, but tests mock that module.
dayjs.extend(utc);

export const shouldDefaultToFutureDate = ({
  applicableData,
  options,
}: {
  applicableData: Pick<TransferContextData, 'type' | 'scheduledDate'>;
  options?: DepositScheduleOptions | null;
}) => {
  if (!options?.allowOneTimeTransfersFutureDate) return false;
  if (applicableData.type !== DEPOSIT_TRANSFER_TYPE.EFT) return false;
  if (applicableData.scheduledDate) return true;
  if (options?.transferDelay?.enabled) return true;
  if (
    options?.allowOneTimeTransfersFutureDate &&
    !options?.allowOneTimeTransfersNow
  )
    return true;
  return false;
};

export const checkTransferDelay = async ({
  scheduledDate,
  days,
  country,
}: {
  scheduledDate?: Date | string;
  days: number | undefined;
  country: string;
}): Promise<TransferDelayCheck> => {
  const today = new Date();
  const minTransferDate = await addBusinessDays(today, days ?? 0, country);

  return {
    dateIsAfter:
      dayjs(scheduledDate).isSame(minTransferDate, 'day') ||
      dayjs(scheduledDate).isAfter(minTransferDate),
    minTransferDate: dayjs(minTransferDate).format(US_DATE_FORMAT),
  };
};

export const isValidTransferDelayCheck = ({
  isEft,
  isScheduled,
  options,
  transferDelayCheck,
  scheduledDateIsBusinessDay,
}: {
  isEft: boolean;
  isScheduled: boolean;
  options?: DepositScheduleOptions | null;
  transferDelayCheck: TransferDelayCheck | null;
  scheduledDateIsBusinessDay: boolean | null;
}) => {
  const transferDelayApplies =
    isEft && isScheduled && !!options?.transferDelay?.enabled;
  if (!transferDelayApplies) return true;

  return (
    !!transferDelayCheck &&
    transferDelayCheck.dateIsAfter &&
    scheduledDateIsBusinessDay === true
  );
};

export const resolveScheduledDateError = ({
  transferDate,
  options,
  transferDelayCheck,
  scheduledDateIsBusinessDay,
}: {
  transferDate?: TransferContextData['scheduledDate'];
  options?: DepositScheduleOptions | null;
  transferDelayCheck: TransferDelayCheck | null;
  scheduledDateIsBusinessDay: boolean | null;
}) =>
  (!transferDate &&
    options?.scheduledDate?.required !== FieldRequiredType.NOT_REQUIRED) ||
  scheduledDateIsBusinessDay === false ||
  (options?.transferDelay?.enabled &&
    !!transferDelayCheck &&
    !transferDelayCheck.dateIsAfter);

export const resolveScheduledDateErrorMessage = ({
  options,
  transferDelayCheck,
}: {
  options?: DepositScheduleOptions | null;
  transferDelayCheck: TransferDelayCheck | null;
}): ScheduledDateErrorMessage => {
  if (
    options?.transferDelay?.enabled &&
    transferDelayCheck &&
    !transferDelayCheck.dateIsAfter
  ) {
    const date = dayjs(transferDelayCheck.minTransferDate).format(
      US_DATE_FORMAT,
    );
    return {
      key: 'bankAccountTransferCreation.transferDelayError',
      params: { date },
    };
  }
  return { key: 'bankAccountTransferCreation.enterAValidDate' };
};

export const resolveDefaultScheduleOption = (
  options?: DepositScheduleOptions | null,
) =>
  options?.editDefaultScheduleFrequency && options?.defaultScheduleOption
    ? options?.defaultScheduleOption
    : undefined;

export const shouldClearScheduledDate = ({
  date,
  options,
  newDelayCheck,
}: {
  date: ConfigType;
  options?: DepositScheduleOptions | null;
  newDelayCheck: TransferDelayCheck;
}) => {
  const isBeforeToday = dayjs(date).isBefore(dayjs(), 'day');
  const failsTransferDelay =
    options?.transferDelay?.enabled && !newDelayCheck.dateIsAfter;

  return isBeforeToday || failsTransferDelay;
};

export const resolveScheduledDateMinDate = ({
  options,
  transferDelayCheck,
}: {
  options?: DepositScheduleOptions | null;
  transferDelayCheck: TransferDelayCheck | null;
}) =>
  options?.transferDelay?.enabled
    ? (transferDelayCheck?.minTransferDate ?? dayjs().add(1, 'day'))
    : dayjs().add(1, 'day');

export const isScheduledDateEditable = ({
  date,
  schedule,
  weekdaysOnlyCalendar,
  transferCountry,
}: {
  date: ConfigType;
  schedule?: Frequency | null;
  weekdaysOnlyCalendar: boolean;
  transferCountry: string;
}) => {
  const dayjsDate = dayjs(date);
  const dayOfMonth = dayjsDate.date();

  // For SEMI_MONTHLY, only allow 1st and 15th
  if (schedule === Frequency.SemiMonthly) {
    return dayOfMonth === 1 || dayOfMonth === 15;
  }

  // For other schedules, use business day logic
  return weekdaysOnlyCalendar
    ? isBusinessDaySync({
        date: dayjsDate.utc().toDate(),
        country: transferCountry,
      })
    : isBusinessDayAndAllowWeekendSync({
        date: dayjsDate.utc().toDate(),
        country: transferCountry,
      });
};
